import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sparkles, Send, Plus, Loader2, Trash2, MessageSquare, Bot, User as UserIcon,
} from 'lucide-react';
import { authHeaders, getAuthToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUGGESTIONS = [
  'Which orders are pending procurement right now?',
  'Show repeated unprocured SKUs (15 days)',
  'Which products are low on stock?',
  'What are this month\u2019s invoiced sales?',
];

// Minimal markdown renderer: **bold**, headings, bullets, `code`.
function renderMarkdown(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => {
    let content = line;
    const isBullet = /^\s*[-*•]\s+/.test(content);
    const isHeading = /^#{1,4}\s+/.test(content);
    if (isBullet) content = content.replace(/^\s*[-*•]\s+/, '');
    if (isHeading) content = content.replace(/^#{1,4}\s+/, '');
    const parts = content.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, j) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={j}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('`') && p.endsWith('`')) {
        return <code key={j} className="bg-slate-100 text-rose-700 px-1 rounded text-[13px] font-mono">{p.slice(1, -1)}</code>;
      }
      return p;
    });
    if (isHeading) return <p key={i} className="font-bold mt-2">{parts}</p>;
    if (isBullet) {
      return (
        <p key={i} className="pl-4 relative">
          <span className="absolute left-0.5">•</span>{parts}
        </p>
      );
    }
    return <p key={i} className={line.trim() === '' ? 'h-2' : ''}>{parts}</p>;
  });
}

function ChatBubble({ role, content, streaming }) {
  const isUser = role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`chat-msg-${role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 mt-1">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        isUser
          ? 'bg-indigo-600 text-white rounded-br-sm'
          : 'bg-white border shadow-sm rounded-bl-sm text-slate-800'
      }`}>
        {isUser ? <p className="whitespace-pre-wrap">{content}</p> : renderMarkdown(content)}
        {streaming && !content && <span className="text-slate-400 italic text-xs">Thinking…</span>}
        {streaming && <span className="inline-block w-2 h-4 bg-indigo-400 animate-pulse ml-0.5 align-text-bottom" />}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-1">
          <UserIcon className="w-4 h-4 text-slate-600" />
        </div>
      )}
    </div>
  );
}

export default function AiAssistant() {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assistant/sessions`, { headers: authHeaders() });
      setSessions(res.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/assistant/history/${sid}`, { headers: authHeaders() });
      setSessionId(sid);
      setMessages((res.data || []).map(m => ({ role: m.role, content: m.content })));
    } catch {
      toast.error('Failed to load conversation');
    }
  };

  const newChat = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/assistant/sessions/${sid}`, { headers: authHeaders() });
      setSessions(s => s.filter(x => x.session_id !== sid));
      if (sid === sessionId) newChat();
    } catch {
      toast.error('Delete failed');
    }
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput('');
    setBusy(true);
    setMessages(m => [...m, { role: 'user', content: message }, { role: 'assistant', content: '', streaming: true }]);
    try {
      abortRef.current = new AbortController();
      const res = await fetch(`${API}/assistant/chat`, {
        method: 'POST',
        signal: abortRef.current.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ session_id: sessionId, message }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              answer += obj.delta;
              setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: answer, streaming: true };
                return copy;
              });
            }
            if (obj.error) throw new Error(obj.error);
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: answer || 'Sorry, I could not generate a response. Please try again.' };
        return copy;
      });
      loadSessions();
    } catch (err) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: `⚠ ${err.message || 'Request failed'}. Please try again.` };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-0px)] flex flex-col" data-testid="ai-assistant-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-indigo-600">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">AI Assistant</h1>
          <p className="text-sm text-slate-600">Ask anything about your stock, orders, procurement, invoices — answered from live ERP data (GPT-5.4)</p>
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Sessions sidebar */}
        <div className="w-60 shrink-0 hidden md:flex flex-col border rounded-lg bg-white overflow-hidden">
          <div className="p-2 border-b">
            <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={newChat}
              data-testid="assistant-new-chat-btn">
              <Plus className="w-4 h-4 mr-1" />New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.length === 0 && (
              <p className="text-xs text-slate-400 p-3">No conversations yet</p>
            )}
            {sessions.map(s => (
              <div key={s.session_id}
                onClick={() => openSession(s.session_id)}
                className={`px-3 py-2 text-xs cursor-pointer border-b flex items-start gap-2 group hover:bg-slate-50 ${
                  s.session_id === sessionId ? 'bg-indigo-50 border-l-2 border-l-indigo-600' : ''
                }`}
                data-testid={`assistant-session-${s.session_id}`}>
                <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium text-slate-700">{s.title || 'Conversation'}</p>
                  <p className="text-[10px] text-slate-400">{String(s.updated_at || '').slice(0, 10)}</p>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                  data-testid={`assistant-delete-${s.session_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col border rounded-lg bg-slate-50 overflow-hidden">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="assistant-chat-area">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center gap-4">
                <div className="p-4 rounded-full bg-indigo-100">
                  <Bot className="w-10 h-10 text-indigo-600" />
                </div>
                <p className="text-slate-600 font-medium">How can I help with your warehouse today?</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-xl">
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)} disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-full border bg-white hover:bg-indigo-50 hover:border-indigo-300 text-slate-700 transition-colors disabled:opacity-50"
                      data-testid="assistant-suggestion-chip">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} content={m.content} streaming={m.streaming && busy} />
            ))}
          </div>
          <div className="p-3 border-t bg-white flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="e.g. What is the current stock of PROD-020601?"
              disabled={busy}
              className="flex-1"
              data-testid="assistant-input"
            />
            <Button onClick={() => send()} disabled={busy || !input.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="assistant-send-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
